import { CloseCircleOutlined, LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Col, Form, Image, Row, Spin, Typography, Upload, message } from "antd";
import React, { useMemo, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useGetOrganization } from "../../../../../api/v1";
import { UPLOAD_CONFIG } from "../../../../libs/constants";
import { formatFileExtensions } from "../../../../libs/utils";
import { buildUploadPath } from "../../../../organism/ImagesAndQrCode/utils";
import { UploadedDocument } from "../../../../../types/document";
import { uploadDocumentMedia } from "../../../../../utils/media";
import { antdBeforeUploadAllowList } from "../../../../../utils/uploadValidation";

const { Text } = Typography;

const DEFAULT_MAX_IMAGE_MB = 10;
const DEFAULT_UNIT_FLOOR_PLAN_MB = 10;
const DEFAULT_UNIT_VIDEO_MB = 25;
const UPLOAD_BOX_HEIGHT = "2.5rem";
const PREVIEW_MAX_HEIGHT = 220;

const resolveOrgUploadLimitMb = (configured: unknown, fallbackMb: number) => {
  const value = Number(configured);
  return Number.isFinite(value) && value > 0 ? value : fallbackMb;
};

const getFieldKey = (fieldName: string) => fieldName.split(".").pop() ?? fieldName;

export type MarketingMediaValue = {
  documentId: string;
  documentUrl: string;
};

type GalleryUploadPathSegments = {
  propertyName?: string;
  unitCode?: string;
};

type MarketingMediaUploadFieldProps = {
  fieldName: string;
  label: string;
  mediaKind: "image" | "video";
  control: Control<any>;
  colSpan?: number;
  disabled?: boolean;
  helperText?: string;
  uploadPathSegments?: GalleryUploadPathSegments;
};

export const MarketingMediaUploadField = ({
  fieldName,
  label,
  mediaKind,
  control,
  colSpan = 12,
  disabled = false,
  uploadPathSegments,
}: MarketingMediaUploadFieldProps) => {
  const { organization } = useGetOrganization({ staleTimeInfinity: true });
  const [uploading, setUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const fieldKey = getFieldKey(fieldName);

  const maxUploadMb = useMemo(() => {
    if (fieldKey === "floorPlanImage") {
      return resolveOrgUploadLimitMb(organization?.unitFloorPlanUploadLimit, DEFAULT_UNIT_FLOOR_PLAN_MB);
    }
    if (fieldKey === "unitVideo") {
      return resolveOrgUploadLimitMb(organization?.unitVideoUploadLimit, DEFAULT_UNIT_VIDEO_MB);
    }
    if (mediaKind === "video") {
      return DEFAULT_UNIT_VIDEO_MB;
    }
    return resolveOrgUploadLimitMb(organization?.marketingImageSize, DEFAULT_MAX_IMAGE_MB);
  }, [
    fieldKey,
    mediaKind,
    organization?.marketingImageSize,
    organization?.unitFloorPlanUploadLimit,
    organization?.unitVideoUploadLimit,
  ]);

  const uploadConfig = useMemo(() => {
    const baseConfig = mediaKind === "video" ? UPLOAD_CONFIG.VIDEO : UPLOAD_CONFIG.IMAGE;
    return {
      ...baseConfig,
      maxBytes: maxUploadMb * 1024 * 1024,
    };
  }, [mediaKind, maxUploadMb]);

  const acceptExtensions = uploadConfig.accept;
  const formatsLabel = formatFileExtensions(acceptExtensions);

  const uploadRowProps = {
    height: UPLOAD_BOX_HEIGHT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px dashed gray",
    transition: "all 0.3s ease-in-out",
    borderRadius: "0.5rem",
    width: "100%",
  };

  const handleUpload = async (file: File, onChange: (value: MarketingMediaValue | null) => void) => {
    const validationResult = await antdBeforeUploadAllowList(file, uploadConfig, (msg) => message.error(msg));
    if (validationResult === Upload.LIST_IGNORE) {
      return Upload.LIST_IGNORE;
    }

    setUploading(true);
    try {
      const path = buildUploadPath(organization?.name, uploadPathSegments);
      const response = await uploadDocumentMedia(path ? { file_asset: file, path } : { file_asset: file }, false);
      const uploadedDocument = response?.data?.result as UploadedDocument;
      if (!uploadedDocument?.documentId) {
        throw new Error("Upload response did not include a documentId");
      }

      const documentUrl = uploadedDocument.documentUrl || "";
      if (!documentUrl) {
        throw new Error("Upload response did not include a documentUrl");
      }

      onChange({
        documentId: uploadedDocument.documentId,
        documentUrl,
      });
      message.success(`${label} uploaded successfully`);
    } catch {
      message.error(`Failed to upload ${file.name}`);
    } finally {
      setUploading(false);
    }

    return Upload.LIST_IGNORE;
  };

  const isFileDragEvent = (event: React.DragEvent<HTMLElement>) => {
    const types = Array.from(event.dataTransfer?.types ?? []);
    return types.includes("Files");
  };

  const getDroppedFiles = (event: React.DragEvent<HTMLElement>) => {
    const items = event.dataTransfer?.items;
    if (items?.length) {
      return Array.from(items)
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((file): file is File => Boolean(file));
    }
    return Array.from(event.dataTransfer?.files ?? []);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
    if (disabled || uploading || !isFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (disabled || uploading || !isFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    if (disabled || uploading || !isFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = async (
    event: React.DragEvent<HTMLElement>,
    onChange: (value: MarketingMediaValue | null) => void,
  ) => {
    if (disabled || uploading || !isFileDragEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    const droppedFiles = getDroppedFiles(event);
    if (droppedFiles.length === 0) {
      return;
    }

    await handleUpload(droppedFiles[0], onChange);
  };

  return (
    <Col span={colSpan}>
      <Controller
        name={fieldName}
        control={control}
        defaultValue={null}
        render={({ field: { onChange, value } }) => {
          const mediaValue = value as MarketingMediaValue | null;
          const hasMedia = Boolean(mediaValue?.documentUrl);

          const handleRemove = () => {
            onChange(null);
            message.info("File removed");
          };

          const uploadInput = (
            <Upload
              accept={acceptExtensions}
              showUploadList={false}
              disabled={disabled || uploading}
              beforeUpload={(file) => handleUpload(file, onChange)}
            >
              <div
                style={{
                  ...uploadRowProps,
                  width: "100%",
                  height: "100%",
                  minHeight: UPLOAD_BOX_HEIGHT,
                  cursor: disabled || uploading ? "not-allowed" : "pointer",
                  opacity: disabled || uploading ? 0.5 : 1,
                }}
              >
                <span style={{ textAlign: "center", padding: "0 10px" }}>
                  <UploadOutlined /> &nbsp; Click to Upload
                </span>
              </div>
            </Upload>
          );

          return (
            <Form.Item label={label} style={{ marginBottom: 24 }}>
              {uploading ? (
                <Row
                  justify="center"
                  style={{
                    ...uploadRowProps,
                    height: PREVIEW_MAX_HEIGHT,
                    background: "#fafafa",
                  }}
                >
                  <Spin indicator={<LoadingOutlined spin />} tip="Uploading..." />
                </Row>
              ) : hasMedia ? (
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      overflow: "hidden",
                      background: mediaKind === "video" ? "#000" : "#fafafa",
                    }}
                  >
                    {!disabled && (
                      <Button
                        type="text"
                        icon={<CloseCircleOutlined />}
                        onClick={handleRemove}
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          color: "#ff4d4f",
                          background: "rgba(255,255,255,0.9)",
                        }}
                      />
                    )}

                    {mediaKind === "image" ? (
                      <Image
                        src={mediaValue!.documentUrl}
                        alt={label}
                        preview
                        width="100%"
                        style={{ display: "block", maxHeight: PREVIEW_MAX_HEIGHT, objectFit: "contain" }}
                      />
                    ) : (
                      <video
                        src={mediaValue!.documentUrl}
                        controls
                        playsInline
                        preload="metadata"
                        style={{
                          width: "100%",
                          maxHeight: PREVIEW_MAX_HEIGHT,
                          display: "block",
                          objectFit: "contain",
                        }}
                      >
                        Your browser does not support video playback.
                      </video>
                    )}
                  </div>
                </div>
              ) : disabled ? (
                <Row
                  justify="center"
                  style={{
                    ...uploadRowProps,
                    width: "100%",
                    height: "6rem",
                    opacity: 0.65,
                    background: "#fafafa",
                  }}
                >
                  <Text type="secondary">No file uploaded</Text>
                </Row>
              ) : (
                <Row
                  justify="center"
                  style={{
                    ...uploadRowProps,
                    width: "100%",
                    height: "6rem",
                    opacity: 0.65,
                    background: isDragActive ? "rgba(22, 119, 255, 0.05)" : "#fafafa",
                  }}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={(event) => handleDrop(event, onChange)}
                >
                  <div
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, width: "100%" }}
                  >
                    <div> {uploadInput}</div>

                    <div>
                      <span style={{ fontSize: "12px" }}>
                        {`only ${formatsLabel} formats are accepted (max ${maxUploadMb}MB).`}
                      </span>
                    </div>
                  </div>
                </Row>
              )}
            </Form.Item>
          );
        }}
      />
    </Col>
  );
};

export default MarketingMediaUploadField;
